import { OFFICE_EMAIL_AUTH_DIALOG_PATH } from './officeEmailAuthConfig';

export type OfficeEmailAuthDialogResult =
    | { status: 'success'; accessToken: string; refreshToken?: string }
    | { status: 'error'; code?: string | number; message?: string };

function getDialogUrl(): string {
    const origin =
        typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';
    return `${origin}${OFFICE_EMAIL_AUTH_DIALOG_PATH}`;
}

export function openOfficeEmailAuthDialog(): Promise<{
    accessToken: string;
    refreshToken?: string;
}> {
    return new Promise((resolve, reject) => {
        const officeUi = (Office as any)?.context?.ui;
        if (!officeUi || typeof officeUi.displayDialogAsync !== 'function') {
            reject(new Error('Office dialog API is not available'));
            return;
        }

        officeUi.displayDialogAsync(
            getDialogUrl(),
            { height: 70, width: 35, displayInIframe: false },
            (asyncResult: any) => {
                if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                    reject(
                        new Error(
                            asyncResult.error?.message ||
                                'Failed to open email sign-in dialog'
                        )
                    );
                    return;
                }

                const dialog = asyncResult.value;

                const cleanup = () => {
                    try {
                        dialog.close();
                    } catch {
                        // dialog may already be closed
                    }
                };

                dialog.addEventHandler(
                    (Office as any).EventType.DialogMessageReceived,
                    (arg: { message?: string; error?: number }) => {
                        if (arg.error) {
                            cleanup();
                            reject(new Error(`Dialog error: ${arg.error}`));
                            return;
                        }

                        try {
                            const msg = JSON.parse(arg.message || '{}') as OfficeEmailAuthDialogResult;
                            cleanup();
                            if (msg.status === 'success' && msg.accessToken) {
                                resolve({
                                    accessToken: msg.accessToken,
                                    refreshToken: msg.refreshToken,
                                });
                                return;
                            }
                            const errorMessage =
                                msg.status === 'error'
                                    ? msg.message || 'Email sign-in did not return a session token'
                                    : 'Email sign-in did not return a session token';
                            reject(new Error(errorMessage));
                        } catch (parseError) {
                            cleanup();
                            reject(parseError);
                        }
                    }
                );

                dialog.addEventHandler(
                    (Office as any).EventType.DialogEventReceived,
                    (arg: { error?: number; message?: string }) => {
                        cleanup();
                        reject(
                            new Error(
                                arg.message ||
                                    `Sign-in dialog closed (error ${arg.error ?? 'unknown'})`
                            )
                        );
                    }
                );
            }
        );
    });
}
